import { config } from '@iheartjane/config';
import kebabCase from 'lodash-es/kebabCase';
import trimEnd from 'lodash-es/trimEnd';
import trimStart from 'lodash-es/trimStart';
import values from 'lodash-es/values';
import qs from 'qs';

/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial, TermsOfServiceVariants } from '@jane/shared/models';

import { placeToParam } from './pathnameHelpers';

type RouteAppMode =
  | { type: 'default' }
  | {
      partnerId: string | number;
      type: 'embedded' | 'whiteLabel' | 'framelessEmbed';
    }
  | {
      partnerId: string | number;
      partnerName?: string;
      type: 'kiosk';
    }
  | {
      type: 'headless' | 'bloom';
    }
  | {
      partnerId: string | number;
      partnerName: string | undefined;
      type: 'kiosk';
    }
  | {
      operatorId: string | number;
      storeId: string | number;
      type: 'operatorEmbed';
    };

const includeStoreSlug = () => appMode.type === 'default';
const STORE_EMBED_APP_MODES = ['framelessEmbed', 'embedded', 'whiteLabel'];

type Query = Record<string, unknown> | string;

const _categories = [
  'edible',
  'extract',
  'flower',
  'pre-roll',
  'tincture',
  'topical',
  'vape',
];

export type Categories = (typeof _categories)[number];
let appMode: RouteAppMode = { type: 'default' };

export const setAppMode = (newAppMode: RouteAppMode) => {
  appMode = newAppMode;
};

type PartnerHostedConfig = {
  isBoostForAll?: boolean;
  isPartnerHosted: boolean;
  partnerHostedPath?: string;
};
let partnerHostedConfig: PartnerHostedConfig = { isPartnerHosted: false };

export const setPartnerHostedConfig = (
  newPartnerHostedConfig: PartnerHostedConfig
) => {
  partnerHostedConfig = newPartnerHostedConfig;
};

const optionalQueryStringPath = (path: string, query?: Query) => {
  if (values(query).filter(Boolean).length === 0) return path;
  return `${path}?${qs.stringify(query)}`;
};

export const absolutePath = (relativePath: string) =>
  [trimEnd(config.deployPublicPath, '/'), trimStart(relativePath, '/')].join(
    '/'
  );

export const objectToGetParams = (obj: object) =>
  `?${Object.entries(obj)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')}`;

interface NameAndSlugAndId {
  id: string | number;
  name?: string;
  slug?: string;
}

interface ProductNameAndSlugAndId extends NameAndSlugAndId {
  brand?: string | null;
}

const getUrlBase = (path: string): string => {
  switch (appMode.type) {
    case 'default':
      return '';
    case 'framelessEmbed': {
      if (partnerHostedConfig.isBoostForAll) return '';

      if (
        partnerHostedConfig.isPartnerHosted &&
        partnerHostedConfig.partnerHostedPath
      ) {
        return partnerHostedConfig.partnerHostedPath;
      }

      return path.startsWith(`/stores/${appMode.partnerId}`)
        ? '/embed'
        : `/embed/stores/${appMode.partnerId}`;
    }
    case 'embedded':
    case 'whiteLabel': {
      if (partnerHostedConfig.isPartnerHosted)
        return `/${partnerHostedConfig.partnerHostedPath}`;
      return path.startsWith(`/stores/${appMode.partnerId}`)
        ? '/embed'
        : `/embed/stores/${appMode.partnerId}`;
    }
    case 'operatorEmbed':
      return path.startsWith(`/stores/${appMode.storeId}`)
        ? `/operator_embed/operators/${appMode.operatorId}`
        : `/operator_embed/operators/${appMode.operatorId}/stores/${appMode.storeId}`;
    case 'kiosk': {
      const name = appMode.partnerName
        ? `/${kebabCase(appMode.partnerName)}`
        : '';
      return `/stores/${appMode.partnerId}${name}`;
    }
    case 'bloom':
      return '';
    case 'headless': {
      return `/headless`;
    }
  }
};

const addUrlBase = (path: string, query?: Query) => {
  const base = getUrlBase(path);

  return optionalQueryStringPath(`${base}${path}`, query);
};

export const monolithPaths = {
  getAppMode() {
    return appMode;
  },
  root() {
    return '/';
  },
  embeddedMenu(query?: Query) {
    return addUrlBase('/menu', query);
  },
  embeddedProduct(query?: Query) {
    return addUrlBase('/', query);
  },
  embeddedProductDetail({
    product,
    fromAllSpecials,
    bundleSpecialId,
    fromSpecialId,
  }: {
    bundleSpecialId?: string | number | null;
    fromAllSpecials?: boolean | null;
    fromSpecialId?: string | number | null;
    product: ProductNameAndSlugAndId;
  }) {
    const parameters = {
      bundleSpecialId,
      fromAllSpecials,
      fromSpecialId,
    };

    if (!fromAllSpecials) {
      delete parameters['fromAllSpecials'];
    }

    return addUrlBase(`/products/${product.id}/${product.slug}`, parameters);
  },
  embeddedLogin() {
    return addUrlBase('/login');
  },
  embeddedLogout() {
    return addUrlBase('/logout');
  },
  embeddedForgotPassword() {
    return addUrlBase('/forgot-password');
  },
  embeddedRegistration() {
    return addUrlBase('/register');
  },
  embeddedProfile() {
    return addUrlBase('/profile');
  },
  embeddedProfileEdit() {
    return addUrlBase('/profile/edit');
  },
  embeddedReservations() {
    return addUrlBase('/profile/reservations');
  },
  embeddedReservation(id: string | number, query?: Query) {
    return addUrlBase(`/profile/reservations/${id}`, query);
  },
  embeddedReservationReceipt(id: string | number) {
    return addUrlBase(`/profile/reservations/${id}/receipt`);
  },
  embeddedReservationStore(id: string | number) {
    return addUrlBase(`/profile/reservations/${id}/store`);
  },
  embeddedIdentification() {
    return addUrlBase('/profile/identification');
  },
  embeddedSettings() {
    return addUrlBase('/settings');
  },
  embeddedPassword() {
    return addUrlBase('/password');
  },
  embeddedReservationReview(reservationData?: any) {
    return addUrlBase(`/reservationReview?${qs.stringify(reservationData)}`);
  },
  embeddedReplaceCart() {
    return addUrlBase('/replaceCart');
  },
  embeddedCartAlerts() {
    return addUrlBase('/cartAlerts');
  },
  menu(store: NameAndSlugAndId, query?: Query) {
    if (['operatorEmbed'].includes(appMode.type)) {
      return addUrlBase(`/stores/${store.id}/menu`, query);
    } else if (appMode.type === 'default') {
      return addUrlBase(
        `/stores/${store.id}/${store.slug ? store.slug : kebabCase(store.name)}`
      );
    }
    return addUrlBase('/menu', query);
  },
  product({
    product,
    slug,
    bundleSpecialId,
  }: {
    bundleSpecialId?: string | number | null;
    product: ProductNameAndSlugAndId;
    slug?: string;
  }) {
    // slug comes from server and used for canonical links for now
    // eventually, product links and canonicals should be the same
    return optionalQueryStringPath(
      `/products/${product.id}/${
        slug
          ? slug
          : kebabCase(`${product.brand ? product.brand : ''}${product.name}`)
      }`,
      { bundleSpecialId }
    );
  },
  products(query?: Query) {
    return addUrlBase(`/products`, query);
  },
  stores(query?: Query) {
    return addUrlBase('/stores', query);
  },
  store(
    store: NameAndSlugAndId,
    query: Query = '',
    slug?: string,
    isEmbeddedMode = false
  ) {
    if (isEmbeddedMode) {
      return addUrlBase(`/stores/${store.id}/menu`, query);
    }
    return addUrlBase(
      `/stores/${store.id}/${slug ? slug : kebabCase(store.name)}/menu`,
      query
    );
  },
  storeAbout(store: NameAndSlugAndId) {
    return `/stores/${store.id}/${kebabCase(store.name)}/about`;
  },
  storeReviews(store: NameAndSlugAndId, slug?: string) {
    return `/stores/${store.id}/${slug ? slug : kebabCase(store.name)}/reviews`;
  },
  storeMenu(store: NameAndSlugAndId, name: string) {
    return `/stores/${store.id}/${kebabCase(store.name)}/menu/${name}`;
  },
  storeProduct({
    store,
    product,
    fromAllSpecials,
    bundleSpecialId,
    fromSpecialId,
  }: {
    bundleSpecialId?: string | number | null;
    fromAllSpecials?: boolean | null;
    fromSpecialId?: string | number | null;
    product: ProductNameAndSlugAndId;
    store: NameAndSlugAndId;
  }) {
    const parameters = {
      bundleSpecialId,
      fromSpecialId,
      fromAllSpecials,
    };
    if (!fromAllSpecials) {
      delete parameters['fromAllSpecials'];
    }
    return optionalQueryStringPath(
      `/stores/${store.id}/${kebabCase(store.name)}/products/${
        product.id
      }/${kebabCase(`${product.brand ? product.brand : ''}${product.name}`)}`,
      parameters
    );
  },
  brandProduct({
    brand,
    product,
    bundleSpecialId,
  }: {
    brand: NameAndSlugAndId;
    bundleSpecialId?: string | number | null;
    product: ProductNameAndSlugAndId;
  }) {
    return optionalQueryStringPath(
      `/brands/${brand.id}/${kebabCase(brand.name)}/products/${
        product.id
      }/${kebabCase(`${product.brand ? product.brand : ''}${product.name}`)}`,
      { bundleSpecialId }
    );
  },

  brandJaneGold() {
    return '/brands/jane-gold';
  },

  category(category: Categories, query?: Query) {
    return addUrlBase(`/categories/${category}`, query);
  },

  collections() {
    return '/collections';
  },

  cart() {
    return addUrlBase('/cart');
  },
  checkout() {
    return addUrlBase('/cart/checkout');
  },

  directory() {
    return addUrlBase('/directory');
  },
  dispensaries({
    country,
    state,
    city,
    coordinates,
    query,
  }: {
    city?: string;
    coordinates?: { lat: string; long: string; zoom: number };
    country?: string;
    query?: Query;
    state?: string;
  }) {
    const cityParam = city && placeToParam(city);
    const stateParam = state && placeToParam(state);
    const countryParam = country && placeToParam(country);

    if (city) {
      return addUrlBase(
        `/dispensaries/${countryParam}/${stateParam}/${cityParam}`,
        query
      );
    }
    if (state) {
      return addUrlBase(`/dispensaries/${countryParam}/${stateParam}`, query);
    }
    if (country) {
      return addUrlBase(`/dispensaries/${countryParam}`, query);
    }

    if (coordinates) {
      return addUrlBase(
        `/dispensaries/coordinates/${coordinates.lat}/${coordinates.long}/${coordinates.zoom}`,
        query
      );
    }

    return addUrlBase('/dispensaries', query);
  },
  guestCheckout() {
    return addUrlBase('/cart/guest_checkout');
  },
  partnerDispensaries() {
    return 'https://dispensaries.iheartjane.com';
  },
  partnerBrands() {
    return `${this.partnerDispensaries()}/brands`;
  },
  brandsLanding(query?: Query) {
    return addUrlBase(`/brands`, query);
  },
  brandProducts(brandId: string | number, brandName: string, query?: Query) {
    return addUrlBase(`/brands/${brandId}/${kebabCase(brandName)}`, query);
  },
  invoice(invoiceId: string | number) {
    return addUrlBase(`/invoice/${invoiceId}`);
  },
  profile() {
    return addUrlBase('/profile');
  },
  profileSettings() {
    return addUrlBase('/profile/settings');
  },
  profileEdit() {
    return addUrlBase('/profile/edit');
  },
  profilePassword() {
    return addUrlBase('/profile/password');
  },
  guestReservationReceipt(cart_uuid: string, query?: Query) {
    return addUrlBase(`/reservation_receipt/${cart_uuid}`, query);
  },
  reservations() {
    return '/profile/reservations';
  },
  reservation(id: string | number, query?: Query) {
    return addUrlBase(`/profile/reservations/${id}`, query);
  },
  reservationStore(id: string | number) {
    return `/profile/reservations/${id}/store`;
  },
  reservationReceipt(id: string | number) {
    return `/profile/reservations/${id}/receipt`;
  },
  special(
    special: Pick<StoreSpecial, 'id' | 'title'>,
    store: NameAndSlugAndId
  ) {
    const specialsPath = `/specials/${special.id}/${kebabCase(special.title)}`;

    if (STORE_EMBED_APP_MODES.includes(appMode.type)) {
      return addUrlBase(specialsPath);
    } else {
      let storePath = `stores/${store.id}`;
      if (includeStoreSlug()) {
        storePath += `/${store?.slug || kebabCase(store?.name)}`;
      }
      return addUrlBase(`/${storePath}${specialsPath}`);
    }
  },
  specials(store: NameAndSlugAndId) {
    if (STORE_EMBED_APP_MODES.includes(appMode.type)) {
      return addUrlBase('/specials');
    } else {
      let storePath = `stores/${store.id}`;
      if (includeStoreSlug()) {
        storePath += `/${store?.slug || kebabCase(store?.name)}`;
      }
      return addUrlBase(`/${storePath}/specials`);
    }
  },
  newProductReview({
    productId,
    productName,
    storeId,
    storeName,
    rating,
  }: {
    productId: string | number;
    productName: string;
    rating?: number;
    storeId?: string | number;
    storeName?: string;
  }) {
    return addUrlBase(
      `/products/${productId}/${encodeURIComponent(productName)}/reviews/new/`,
      { storeId, storeName, rating }
    );
  },
  identification() {
    return addUrlBase('/profile/identification');
  },
  termsOfUse(termsType: TermsOfServiceVariants = 'customer') {
    switch (termsType) {
      case 'customer':
        return '/terms';
      case 'brands':
        return '/brands-terms';
      case 'data':
        return '/data-terms';
      case 'business':
        return '/dispensary-terms';
      default:
        return '/terms';
    }
  },
  accessibility() {
    return '/ada';
  },
  privacyPolicy() {
    return '/privacy-policy';
  },
  doNotSellMyInformation() {
    return '/do-not-sell-my-information';
  },
  contactUs() {
    return '/profile/contactUs';
  },
  forgotPassword() {
    return '/forgot-password';
  },
  resetPassword() {
    return '/reset-password';
  },
  login() {
    return '/login';
  },
  maintenance() {
    return '/maintenance';
  },
  kioskMenu(query?: Query) {
    return addUrlBase('/menu', query);
  },
  kioskLanding() {
    return addUrlBase('/landing');
  },
  kioskSetup() {
    return addUrlBase('/setup');
  },
  kioskThankyou() {
    return addUrlBase('/thankyou');
  },
  kioskQRThankyou(cart_uuid: string) {
    return addUrlBase(`/cart/${cart_uuid}/thankyou`);
  },
  headlessProductDetail(product: ProductNameAndSlugAndId) {
    return addUrlBase(`/products/${product.id}/${product.slug}`);
  },
} as const;
